/**
 * NAD Promo Bar Component
 * Creates a marquee/scroll animation for the promotion bar
 */

export class NADPromoBar {
  private element: HTMLElement;
  private marquee: HTMLElement | null;
  private contents: NodeListOf<HTMLElement>;
  private animationSpeed: number = 30;
  private mouseEnterHandler: () => void;
  private mouseLeaveHandler: () => void;
  private resizeHandler: () => void;

  constructor(element: HTMLElement) {
    this.element = element;
    this.marquee = element.querySelector('[data-promo-bar]');
    this.contents = element.querySelectorAll('.nad-promo-bar__content');
    
    this.mouseEnterHandler = () => this.pauseAnimation();
    this.mouseLeaveHandler = () => this.resumeAnimation();
    this.resizeHandler = () => this.updateAnimation();
    
    if (this.contents.length > 0) {
      this.init();
    }
  }

  private init(): void {
    // Get animation speed from data attribute or default
    const speedAttr = this.element.getAttribute('data-animation-speed');
    if (speedAttr) {
      this.animationSpeed = parseFloat(speedAttr);
    }

    // Apply animation to all content elements
    this.contents.forEach((content) => {
      content.style.animationDuration = `${this.animationSpeed}s`;
    });

    // Pause on hover for better UX
    this.marquee?.addEventListener('mouseenter', this.mouseEnterHandler);
    this.marquee?.addEventListener('mouseleave', this.mouseLeaveHandler);

    // Handle resize events to recalculate animation
    window.addEventListener('resize', this.resizeHandler);
  }

  private pauseAnimation(): void {
    this.contents.forEach((content) => {
      content.style.animationPlayState = 'paused';
    });
  }

  private resumeAnimation(): void {
    this.contents.forEach((content) => {
      content.style.animationPlayState = 'running';
    });
  }

  private updateAnimation(): void {
    // Reapply animation speed after resize
    this.contents.forEach((content) => {
      content.style.animationDuration = `${this.animationSpeed}s`;
    });
  }

  public destroy(): void {
    this.marquee?.removeEventListener('mouseenter', this.mouseEnterHandler);
    this.marquee?.removeEventListener('mouseleave', this.mouseLeaveHandler);
    window.removeEventListener('resize', this.resizeHandler);
  }

  // Static method for automatic initialization
  static initializeAll(): void {
    const elements = document.querySelectorAll<HTMLElement>('.nad-promo-bar');
    elements.forEach((element) => {
      if (!(element as any)._nadPromoBarInstance) {
        (element as any)._nadPromoBarInstance = new NADPromoBar(element);
      }
    });
  }

  // Static method for cleanup
  static destroySection(element: HTMLElement): void {
    const instance = (element as any)._nadPromoBarInstance;
    if (instance && typeof instance.destroy === 'function') {
      instance.destroy();
      delete (element as any)._nadPromoBarInstance;
    }
  }
}

